import { useMemo } from 'react';

/**
 * @typedef {Object} StoreHour
 * @property {string} day - Day of the week (monday, tuesday, etc.)
 * @property {string} startTime - Start time in HH:mm format
 * @property {string} endTime - End time in HH:mm format
 */

/**
 * @typedef {Object} Store
 * @property {Array<StoreHour>} [hours] - Array of store hours
 */

const DAYS = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
];

const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
};

/**
 * Whether a store is open at `now` (local time) according to its hours. No
 * configured hours = always open (same criterion as the mobile app's
 * computeOpen — don't lock out stores that never set hours); no hours for
 * today = closed. Hours are stored in local timezone, so no conversion.
 *
 * @param {Store} store - Store object with hours property
 * @param {Date} [now]
 * @returns {boolean}
 */
export const isStoreOpen = (store, now = new Date()) => {
    if (!store?.hours || store.hours.length === 0) return true;

    const today = DAYS[now.getDay()];
    const current = now.getHours() * 60 + now.getMinutes();

    return store.hours
        .filter((h) => h.day === today)
        .some(
            (h) =>
                current >= timeToMinutes(h.startTime) &&
                current <= timeToMinutes(h.endTime)
        );
};

/**
 * Custom hook to check if a store is currently open based on its hours
 *
 * @param {Store} store - Store object with hours property
 * @returns {boolean} - True if store is open, false otherwise
 */
export const useStoreHours = (store) =>
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useMemo(() => isStoreOpen(store), [store?.hours]);
